/**
 * §5-bis frontmatter writeback — persist a CONFIRMED relation into the source
 * record's dedicated `ontos:` frontmatter block, so confirmed knowledge survives
 * cross-machine via git (the index is machine-local).
 *
 * SAFETY (why this was deferred — these are the USER's files):
 *   - only CONFIRMED relations reach here;
 *   - we edit ONLY the `ontos:` key, via `yaml.parseDocument` (a format/comment-
 *     preserving round-trip) — NOT regex splicing, which mis-handles column-0 YAML
 *     lists and can corrupt frontmatter (Codex review);
 *   - if the frontmatter does not parse, or `ontos:` already exists as something
 *     that is NOT our list of {relId,…} entries (a user's own key), we ABORT and
 *     touch nothing — never destroy user data on a collision/malformed file;
 *   - idempotent by relId; the file's CRLF/LF style is preserved; atomic write.
 */

import { createHash } from "node:crypto";
import { existsSync, readFileSync, writeFileSync, renameSync, realpathSync } from "node:fs";
import { resolve, relative, isAbsolute } from "node:path";
import { parseDocument, Document, isSeq } from "yaml";
import type { RelationType } from "./types.js";
import type { OntosStore } from "./sqlite/store.js";

const FENCE = /^(---\r?\n)([\s\S]*?)(\r?\n---\r?\n?)/;
const BOM = "﻿";

/**
 * One confirmed relation to persist (the §5-bis entry shape). It is SELF-CONTAINED:
 * it carries both the `source` and `target` NODE ids so a deterministic rebuild can
 * re-derive the confirmed relation from the block alone (the source node used to be
 * left implicit = the record's own object, which the rebuild reader could not recover —
 * that broke the round-trip). `provenance` is the relation's origin (was the misnamed
 * `source` field). Field names match what the rebuild reader (`fromOntosBlock`) expects.
 */
export interface WritebackEntry {
  readonly relId: string;
  readonly rel: RelationType;
  readonly source: string; // source NODE id
  readonly target: string; // target NODE id
  readonly provenance: "deterministic" | "llm" | "wikilink-promoted";
  readonly confidence: number;
  readonly confirmed: string; // ISO date
}

export type WritebackStatus =
  | "added"
  | "present" // relId already recorded — idempotent no-op
  | "created-frontmatter" // file had no frontmatter; a fence holding only our block was added
  | "skipped-unparseable" // frontmatter doesn't parse / is ambiguous — left untouched
  | "skipped-ontos-collision"; // a non-ours `ontos:` key exists — left untouched

function entryNode(e: WritebackEntry): Record<string, unknown> {
  return { relId: e.relId, rel: e.rel, source: e.source, target: e.target, provenance: e.provenance, confidence: e.confidence, confirmed: e.confirmed };
}

/** Does the existing `ontos` value look like OUR list (a seq of objects carrying relId)? */
function isOurOntos(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every((e) => e && typeof e === "object" && "relId" in (e as object));
}

/**
 * Add (idempotently) a confirmed relation to a record's `ontos:` block. Pure: takes
 * the file text, returns the new text + what happened. Uses parseDocument so user
 * keys, comments, and formatting round-trip; aborts (touches nothing) on a
 * malformed frontmatter or an `ontos:` key that is not ours.
 */
export function writebackRelation(
  fileText: string,
  entry: WritebackEntry,
): { text: string; status: WritebackStatus } {
  const bom = fileText.startsWith(BOM) ? BOM : "";
  const text = bom ? fileText.slice(BOM.length) : fileText;
  const m = text.match(FENCE);

  if (!m) {
    // An opener with no clean fence match (e.g. empty `---\n---\n`, or a malformed
    // fence) is AMBIGUOUS — adding our own fence would double-fence and corrupt the
    // file. Only synthesize a fence when there is genuinely no leading fence at all.
    if (/^---\r?\n/.test(text)) {
      return { text: fileText, status: "skipped-unparseable" };
    }
    // No frontmatter — synthesize a minimal fence holding only our block.
    const doc = new Document({ ontos: [entryNode(entry)] });
    const block = doc.toString().replace(/\n+$/, "");
    return { text: `${bom}---\n${block}\n---\n${text}`, status: "created-frontmatter" };
  }

  const open = m[1] ?? "---\n";
  const close = m[3] ?? "\n---\n";
  const fmText = m[2] ?? "";
  const body = text.slice(m[0].length);
  const crlf = /\r\n/.test(open) || /\r\n/.test(fmText);

  const doc = parseDocument(fmText);
  if (doc.errors.length > 0) {
    return { text: fileText, status: "skipped-unparseable" }; // never edit a file we can't parse
  }
  const curNode = doc.get("ontos");
  const cur = curNode == null ? undefined : (isSeq(curNode) ? (curNode.toJSON() as unknown) : (curNode as unknown));
  if (cur !== undefined && !isOurOntos(cur)) {
    return { text: fileText, status: "skipped-ontos-collision" }; // a user's own `ontos:` — don't clobber
  }
  const existing = (cur as Record<string, unknown>[] | undefined) ?? [];
  if (existing.some((e) => e["relId"] === entry.relId)) {
    return { text: fileText, status: "present" }; // idempotent
  }

  doc.set("ontos", [...existing, entryNode(entry)]);
  let newFm = doc.toString().replace(/\n+$/, "");
  if (crlf) newFm = newFm.replace(/\r?\n/g, "\r\n"); // keep the file's line-ending style
  return { text: `${bom}${open}${newFm}${close}${body}`, status: "added" };
}

/**
 * Atomic fs wrapper: read → transform → write (same-dir temp + rename). Skips the
 * write when nothing changed (present / skipped-*). Returns the status.
 */
export function writebackToFile(path: string, entry: WritebackEntry): WritebackStatus {
  const before = readFileSync(path, "utf8");
  const { text, status } = writebackRelation(before, entry);
  if (text === before) return status; // present / skipped-* — nothing to write
  // Lost-update guard: if the file changed since we read it (a concurrent edit or
  // writeback), abort rather than clobber it. Shrinks the read-transform-write
  // window to the gap between this re-read and the rename (single-agent use makes
  // even that practically nil); full cross-process safety would need a lock.
  if (readFileSync(path, "utf8") !== before) {
    throw new Error("record changed on disk during writeback — aborted to avoid overwriting it");
  }
  const tmp = `${path}.ontos-${createHash("sha1").update(text).digest("hex").slice(0, 8)}.tmp`;
  writeFileSync(tmp, text, "utf8");
  renameSync(tmp, path); // atomic on the same filesystem
  return status;
}

export interface ConfirmWritebackResult {
  readonly confirmed: boolean;
  readonly writeback: WritebackStatus | "skipped-no-record" | "skipped-out-of-scope" | "error" | null;
  readonly recordPath: string | null;
  readonly message?: string;
}

/**
 * Confirm a proposal AND persist it to the source record's `ontos:` block (the
 * user's chosen auto-on-confirm). The store confirm is the durable step; the file
 * writeback is best-effort — a missing/unreadable/out-of-scope record never fails
 * the confirm (the relation is still in the index), it is reported instead. Pass
 * `dataRoot` to bound writes under the data dir (defense against a record path that
 * somehow points outside it). The agent calls THIS, not bare confirmProposal.
 */
export function confirmAndWriteback(
  store: OntosStore,
  relId: string,
  date: string,
  opts?: { readonly dataRoot?: string },
): ConfirmWritebackResult {
  const p = store.getProposal(relId);
  const ok = store.confirmProposal(relId, date);
  if (!ok || !p) {
    return { confirmed: ok, writeback: null, recordPath: null, message: ok ? "" : "not confirmed (already confirmed/rejected, unresolved ends, or domain/range)" };
  }
  const recordPath = p.evidencePath || null;
  if (!recordPath || !existsSync(recordPath)) {
    return { confirmed: true, writeback: "skipped-no-record", recordPath, message: "confirmed in the index; source record absent — no writeback" };
  }
  if (opts?.dataRoot) {
    // Symlink-SAFE containment: a lexical resolve()+relative() alone is bypassable by
    // a symlink `data/x.md -> /outside` (Codex review) — resolve() normalizes `..` but
    // does NOT follow links. realpathSync resolves the REAL paths, so a record whose
    // real location is outside the data root is caught. (A plain dangling symlink is
    // already handled above — existsSync follows it and returns false → skipped-no-record.)
    // The try/catch here is defense-in-depth for a realpath that fails another way
    // (a TOCTOU break, a permission error): treat as out-of-scope, never write.
    let realRoot, realRecord;
    try {
      realRoot = realpathSync(resolve(opts.dataRoot));
      realRecord = realpathSync(resolve(recordPath));
    } catch {
      return { confirmed: true, writeback: "skipped-out-of-scope", recordPath, message: "confirmed in the index; record path unresolvable (broken symlink?) — no writeback" };
    }
    const rel = relative(realRoot, realRecord);
    if (rel.startsWith("..") || isAbsolute(rel)) {
      return { confirmed: true, writeback: "skipped-out-of-scope", recordPath, message: "confirmed in the index; record is outside the data dir — no writeback" };
    }
  }
  try {
    const status = writebackToFile(recordPath, {
      relId: p.relId,
      rel: p.rel,
      source: p.sourceId,
      target: p.targetId,
      provenance: "llm",
      confidence: p.confidence,
      confirmed: date,
    });
    return { confirmed: true, writeback: status, recordPath };
  } catch (e) {
    return { confirmed: true, writeback: "error", recordPath, message: `confirmed in the index; writeback failed: ${(e as Error)?.message ?? String(e)}` };
  }
}
